import React from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Typography } from "@mui/material";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { currencySign } from "constants/index";
import { MRT_Row } from "material-react-table";
import { CurrencyType, DealAssetSimple, DealBarCharacteristic, DealsTable, DealsTableCell } from "store/types";
import { formatDate } from "utils";
import { formatCurrency } from "utils/strings";

import { DEAL_NAME_COLUMN_KEY } from "./dealGroupedTable.config";

export type DealRowData = DealsTable["rows"][number];

/**
 * Raw joined characteristic value — used for sorting and group labels (accessorFn / cellStr). Kept UNformatted
 * so e.g. ISO dates still sort chronologically; display formatting lives in `formatCharacteristicDisplay`.
 */
export const characteristicDisplay = (char: DealBarCharacteristic): string | null => {
  const joined = char.data
    .map((item) => item.value)
    .filter(Boolean)
    .join(", ");
  return joined || null;
};

/** Format one characteristic value for DISPLAY by its data type: date → account format, `%`, currency sign. */
const formatCharacteristicValue = (
  dataType: DealBarCharacteristic["data_type"],
  item: DealBarCharacteristic["data"][number],
  dateFormat?: string
): string => {
  const raw = item.value;
  if (!raw) return "";
  switch (dataType) {
    case "date":
      return formatDate({ date: raw, format: dateFormat, empty: "" });
    case "percentage":
      return `${raw}%`;
    case "currency": {
      const numeric = Number(String(raw).replace(/,/g, ""));
      // The built-in "Currency" characteristic stores a code (e.g. "USD") in `value`, not an amount — show as-is
      // (no currency sign glued in front). Only genuine amounts get the sign + thousands separators.
      if (Number.isNaN(numeric)) return raw;
      const symbol = item.currency ? (currencySign[item.currency as CurrencyType] ?? "") : "";
      return formatCurrency({ value: numeric, currencySymbol: symbol });
    }
    default:
      return raw;
  }
};

/** Display-formatted joined value for characteristic CELLS (date / percentage / currency) — used by CharacteristicCell. */
export const formatCharacteristicDisplay = (char: DealBarCharacteristic, dateFormat?: string): string | null => {
  const joined = char.data
    .map((item) => formatCharacteristicValue(char.data_type, item, dateFormat))
    .filter(Boolean)
    .join(", ");
  return joined || null;
};

export const cellStr = (value: DealsTableCell | undefined): string | null => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "object") {
    return Array.isArray((value as DealBarCharacteristic).data)
      ? characteristicDisplay(value as DealBarCharacteristic)
      : (value as DealAssetSimple).name || null;
  }
  return String(value);
};

export const ColumnHeader: React.FC<{ title: string }> = ({ title }) => (
  <MaterialTooltip title={title} isShow>
    <Typography noWrap sx={{ fontSize: "14px", fontWeight: 500, color: "#071f35" }}>
      {title}
    </Typography>
  </MaterialTooltip>
);

const groupLabel = (row: DealRowData, keys: string[]): string => {
  for (const key of keys) {
    const str = cellStr(row[key]);
    if (str) return str;
  }
  return "";
};

export const DealNameCell: React.FC<{
  row: MRT_Row<DealRowData>;
  labelKeys: string[];
  onToggle?: (key: string) => void;
}> = ({ row, labelKeys, onToggle }) => {
  const group = row.getCanExpand();
  const text =
    (group
      ? (cellStr(row.original["group_title"]) ?? groupLabel(row.original, labelKeys))
      : cellStr(row.original[DEAL_NAME_COLUMN_KEY])) || "—";

  const label = (
    <Typography
      title={text}
      sx={{
        fontSize: "15px",
        lineHeight: "24px",
        fontWeight: group ? 700 : 400,
        color: group ? (row.depth === 0 ? "#071f35" : "#496682") : "#26a6ae",
        cursor: group ? "default" : "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", pl: `${row.depth * 20}px`, minWidth: 0 }}>
      {group && (
        <IconButton
          size="sm"
          aria-label={row.getIsExpanded() ? "collapse group" : "expand group"}
          onClick={() => onToggle?.(row.id)}
          sx={{ color: "#496682", mr: "4px" }}
        >
          {row.getIsExpanded() ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
      )}
      {group ? (
        label
      ) : (
        <NavigateLink allowNewTab route="deal" params={{ deal_id: row.original.id }} style={{ minWidth: 0 }}>
          {label}
        </NavigateLink>
      )}
    </Box>
  );
};

export const ValueCell: React.FC<{ value: string | null; variant: "link" | "text" }> = ({ value, variant }) => {
  const color = variant === "link" ? "#26a6ae" : "#071f35";

  if (value === null) return null;
  return (
    <MaterialTooltip title={value} isShow>
      <Typography
        noWrap
        sx={{
          display: "block",
          fontSize: "15px",
          lineHeight: "24px",
          color,
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {value}
      </Typography>
    </MaterialTooltip>
  );
};
